import { cloneDeep } from 'lodash'

/**
 * Column preference persistence helpers for the ColumnConfigurator
 * (VF-4175, epic VF-4136). Pure functions only — no store, no HTTP.
 *
 * Storage format (PRD "defaults-only overrides"), one entry per table under
 * the single `tableColumns` user setting:
 *   { version: <int>, columns: [{ id, visible?: false, primary?: true, hideable?: false }] }
 * Array order = display order. `visible` stored only when false; `primary`
 * only when true; `hideable` only when false. On load the code-defined
 * registry is authoritative for primary/hideable — saved values are
 * informational only.
 *
 * DORMANT entries (VF-4416): registries are data-dependent (`product_{id}`
 * columns vary per route / result set), so a saved id can be legitimately
 * absent from the registry currently in effect. Such an entry is DORMANT:
 * kept in the stored array at its position (carrying only `id` and, when
 * false, `visible` — primary/hideable cannot be re-derived and are never
 * invented), never rendered, never shown in the configurator. When the id
 * returns to a later registry, the entry renders again at its saved position
 * with its saved visibility. Dormant entries are removed only by a version
 * bump of the table (the deliberate escape hatch when a column is genuinely
 * retired), by Reset to Defaults (which deletes the whole entry), or by the
 * per-table cap below.
 */
export const TABLE_COLUMNS_SETTING_KEY = 'tableColumns'

/**
 * Per-table ceiling on dormant entries. The whole tableColumns object for
 * every table shares one 65,535-byte TEXT row, so retention must be bounded:
 * on overflow the dormant entries furthest down the stored array (furthest
 * from the visible block) are evicted, in array order — deterministic, no
 * timestamps, no storage-shape change. 100 is ~10x the largest plausible
 * registry and ~2KB per table.
 */
export const DORMANT_COLUMNS_CAP = 100

/**
 * Serialize a resolved column registry (VF-4174 shape) into its
 * defaults-only storage form.
 *
 * @param {Array} columns ordered registry [{ id, label, visible, primary, hideable }]
 * @return {Array} storage entries
 */
export function serializeColumns (columns) {
  return columns.map(column => {
    const entry = { id: column.id }
    if (column.primary === true) {
      entry.primary = true
    }
    if (column.hideable === false) {
      entry.hideable = false
    }
    // visible is an override only when false, and never for columns that
    // cannot be hidden (primary / non-hideable)
    if (column.visible === false && column.primary !== true && column.hideable !== false) {
      entry.visible = false
    }
    return entry
  })
}

/**
 * Merge a table's saved config into its current default registry (VF-4201).
 * Deterministic and idempotent: resolve(resolve(x)) === resolve(x).
 *
 * Rules (PRD edge cases, amended by VF-4416):
 *  - saved order wins for columns still in the registry
 *  - saved column id not in the registry -> DORMANT: excluded from the
 *    returned render list but retained (with its array index) in `dormant`,
 *    does NOT flag the entry as changed. Exceptions that do drop it and flag
 *    changed: a version mismatch (the escape hatch for genuinely retired
 *    columns) and overflow of DORMANT_COLUMNS_CAP (deterministic eviction,
 *    furthest-down first)
 *  - duplicate saved ids (renderable or dormant) -> first occurrence wins,
 *    flagged as changed so the convergence write repairs storage (VF-4223)
 *  - registry column missing from the saved set (new column) -> inserted
 *    after the last visible column, with its DEFAULT visibility
 *  - primary always forced to position 0 and visible; non-hideable forced
 *    visible; registry is authoritative for label/primary/hideable
 *  - no saved entry, or a saved entry of the wrong shape -> pure defaults
 *
 * @param {Array} defaultColumns the table's built-in registry (never mutated)
 * @param {Object|undefined} savedEntry { version, columns } from storage
 * @param {Number} version the table's CURRENT schema version
 * @return {{ columns: Array, dormant: Array, changed: Boolean }} resolved
 *   registry; `dormant` = [{ index, column: { id, visible? } }] saved entries
 *   absent from the registry, to be re-attached by mergeDormantColumns before
 *   any write; `changed` = the stored entry is stale (duplicate ids, added
 *   columns, dormant overflow, or a version mismatch) and should be converged
 *   by writing the resolved form back
 */
export function resolveColumns (defaultColumns, savedEntry, version) {
  const validEntry = savedEntry &&
    typeof savedEntry === 'object' &&
    Array.isArray(savedEntry.columns)

  if (!validEntry) {
    return { columns: cloneDeep(defaultColumns), dormant: [], changed: false }
  }

  const defaultsById = {}
  defaultColumns.forEach(column => { defaultsById[column.id] = column })

  // 1. Saved order. Ids the registry does not know become dormant (retained,
  // not rendered) — unless the version is stale, which drops them (escape
  // hatch). Duplicates are always dropped. `skipped` records that storage held
  // something we dropped, one of the signals that the entry needs converging.
  const staleVersion = savedEntry.version !== version
  const seen = {}
  const resolved = []
  const dormant = []
  let skipped = false
  savedEntry.columns.forEach((saved, index) => {
    const registryColumn = defaultsById[saved.id]
    if (seen[saved.id]) {
      skipped = true
      return
    }
    if (!registryColumn) {
      seen[saved.id] = true
      if (staleVersion || dormant.length >= DORMANT_COLUMNS_CAP) {
        skipped = true
        return
      }
      // Only id and a false visibility survive; primary/hideable belong to
      // the registry and cannot be re-derived for an absent column.
      const column = { id: saved.id }
      if (saved.visible === false) {
        column.visible = false
      }
      dormant.push({ index, column })
      return
    }
    seen[saved.id] = true
    const column = cloneDeep(registryColumn)
    // Only the visibility choice comes from storage; enforce the matrix
    column.visible = saved.visible !== false
    if (column.primary === true || column.hideable === false) {
      column.visible = true
    }
    resolved.push(column)
  })

  // 2. New registry columns -> after the last visible column, default visibility
  let inserted = false
  defaultColumns.forEach(registryColumn => {
    if (seen[registryColumn.id]) {
      return
    }
    inserted = true
    const column = cloneDeep(registryColumn)
    let insertAt = 0
    resolved.forEach((existing, index) => {
      if (existing.visible) {
        insertAt = index + 1
      }
    })
    resolved.splice(insertAt, 0, column)
  })

  // 3. Primary pinned at position 0
  const primaryIndex = resolved.findIndex(column => column.primary === true)
  if (primaryIndex > 0) {
    resolved.unshift(resolved.splice(primaryIndex, 1)[0])
  }

  // Steps 1 and 2 already know everything convergence needs, so derive it from
  // them rather than re-scanning: `skipped` covers dropped ids (duplicates,
  // stale-version prunes, dormant overflow), `inserted` covers new registry
  // columns. Dormant retention alone must NOT converge — every mount on a
  // route with a different column set would otherwise rewrite storage (an
  // audit-log row and a cross-tab refetch per navigation).
  const changed = skipped || inserted || staleVersion

  return { columns: resolved, dormant, changed }
}

/**
 * Re-attach dormant entries to a serialized column array before it is written
 * to storage (VF-4416). The configurator and the render path only ever see
 * renderable columns, so every save must merge the dormant set back in or the
 * first edit after mount would silently drop it. Each dormant entry returns
 * to its remembered index (clamped to the array length), ascending, which
 * preserves the stored relative order.
 *
 * @param {Array} serializedColumns output of serializeColumns (never mutated)
 * @param {Array} dormant [{ index, column }] from resolveColumns
 * @return {Array} storage-ready column entries
 */
export function mergeDormantColumns (serializedColumns, dormant) {
  const merged = serializedColumns.slice()
  dormant.forEach(({ index, column }) => {
    merged.splice(Math.min(index, merged.length), 0, cloneDeep(column))
  })
  return merged
}
